import React, {
  Dispatch,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useDebouncedCallback } from "use-debounce";
import { Auth0ContextInterface } from "@auth0/auth0-react";
import { useContext, createContext } from "react";
import { v4 as uuidv4 } from "uuid";
import { useFeature } from "./FeatureFlags";
import useLocalStorage, {
  compressImage,
  getFilterProperties,
  parseProcessingResponse,
  getMean,
  isMatchingDamage,
} from "../services/Utility";
import { useHistory } from "react-router";
import {
  ViImageStatus,
  ViImageData,
  ViCoreProcessing,
  Filter,
  ViImagePartDamage,
  ViAggregatedPartDamage,
  ViClaimDamagedPart,
  ViCarModel,
  ViClaimTotalLoss,
  ViPhysicalPart,
} from "../models";
import { BundlePart } from "./PartsterContext";
import { loadViProcessingState, storeViProcessingState } from "../services/Storage";
import { AppContext, useApp } from "./AppContext";
import { History } from "history";
import { ViError, ViErrorType, ViUnableToLoadResultsFromStorage } from "../models/Errors";
import { ViCoreProvider } from "./ViCoreSubContext";
import { ViInferenceProvider } from "./ViInferenceSubContext";

export interface ViState {
  status: ViActionType | "READY";
  caseId?: string;
  processingId?: string;
  isLoading: boolean;
  images: ViImageData[];
  aggregatedPartDamages: ViAggregatedPartDamage[];
  claimDamagedParts: ViClaimDamagedPart[];
  bundleParts: BundlePart[];
  carModel?: ViCarModel;
  meanConfidence?: number;
  numberOfDamagedParts?: number;
  totalLoss?: ViClaimTotalLoss;
  timing?: number;
  thresholds: ViThresholds;
  footerTab: string | null;
  hoverFilter: Filter[];
}

export interface ViThresholds {
  damagedPartThreshold?: number;
  globalThreshold: number;
}

interface ViContext extends ViState {
  dispatch: (action: ViAction) => void;
  actions: ViAction[];
  onImageLoad: (files: File[], add?: boolean) => void;
  onImageDelete: (image: ViImageData) => void;
  setHoverFilter: (filters: Filter[]) => void;
  onConfidenceThresholdChange: (thresholds: Partial<ViThresholds>) => void;
  setFooterTab: (newState: string | null) => void;
  toggleShowDuplicates: () => void;
  toggleShowUndamaged: () => void;
  toggleShowRejected: () => void;
  resetState: () => void;
  isFilterMatch: (filterProps: Filter[]) => boolean | undefined;
  showUndamaged: boolean;
  showRejected: boolean;
  showDuplicates: boolean;
}

export enum ViActionType {
  ImagesAdded = "IMAGES - ADDED",
  ImagesLoading = "IMAGES - LOADING",
  ImageAdded = "IMAGE - ADDED",
  ImageLoading = "IMAGE - LOADING",
  ImageCompressing = "IMAGE - COMPRESSING",
  ImageLoaded = "IMAGE - LOADED",
  ImageProcessing = "IMAGE - PROCESSING",
  ImageUploaded = "IMAGE - UPLOADED",
  ImageDeleted = "IMAGE - DELETED",
  ImagesLoaded = "IMAGES - LOADED",
  ImagesChanged = "IMAGES - CHANGED",
  ViResultsReceived = "VI RESULTS RECEIVED",
  ViResultsParsed = "VI RESULTS PARSED",
  PartsterResultsReceived = "PARTSTER RESULTS RECEIVED",
  LoadResultsFromStorage = "LOAD RESULTS FROM STORAGE",
  LoadResultsFromStorageSuccess = "LOAD RESULTS FROM STORAGE SUCCESS",
  ThresholdChanged = "THRESHOLD CHANGED",
  SetHoverFilter = "SET HOVER FILTER",
  ChangeFooterState = "CHANGE FOOTER STATE",
  Error = "ERROR",
  Reset = "RESET",
}

export type ViAction =
  | {
      type: ViActionType.ImagesAdded;
      payload: { imagesToUpload: ViImageData[]; adding?: boolean };
    }
  | { type: ViActionType.ImagesLoading }
  | { type: ViActionType.ImageAdded; payload: { image: ViImageData } }
  | { type: ViActionType.ImageCompressing; payload: { image: ViImageData } }
  | { type: ViActionType.ImageLoaded; payload: { image: ViImageData } }
  | { type: ViActionType.ImageProcessing; payload: { image: ViImageData } }
  | { type: ViActionType.ImageUploaded; payload: { image: ViImageData } }
  | {
      type: ViActionType.ImageDeleted;
      payload: { deletedImage: ViImageData; newImages?: ViImageData[] };
    }
  | { type: ViActionType.ImagesLoaded; payload: { images: ViImageData[] } }
  | { type: ViActionType.ImagesChanged }
  | {
      type: ViActionType.ViResultsReceived;
      payload: {
        processingResponse: ViCoreProcessing;
        caseId?: string;
        processingId?: string;
      };
    }
  | {
      type: ViActionType.ViResultsParsed;
      // payload: {
      //   parsedProcessing: ViProcessingResult;
      //   caseId?: string;
      // };
    }
  | {
      type: ViActionType.PartsterResultsReceived;
      payload: {
        bundleParts: BundlePart[];
      };
    }
  | {
      type: ViActionType.LoadResultsFromStorage;
      payload: { caseId: string; processingId: string };
    }
  | {
      type: ViActionType.LoadResultsFromStorageSuccess;
      payload: { viState: ViState; caseId: string; processingId: string };
    }
  | {
      type: ViActionType.ThresholdChanged;
      payload: { globalThreshold?: number; damagedPartThreshold?: number };
    }
  | { type: ViActionType.ChangeFooterState; payload: { newState: string | null } }
  | {
      type: ViActionType.SetHoverFilter;
      payload: { filters: Filter[] | ViImagePartDamage[] };
    }
  | { type: ViActionType.Error; payload: { error: ViError } }
  | { type: ViActionType.Reset };

const initialState = {
  status: "READY" as const,
  meanConfidence: undefined,
  numberOfDamagedParts: undefined,
  isLoading: false,
  hoverFilter: [],
  thresholds: {
    globalThreshold: 0.4,
  },
  footerTab: null,
  images: [],
  aggregatedPartDamages: [],
  claimDamagedParts: [],
  bundleParts: [],
};

const ViContext = createContext<ViContext | undefined>(undefined);
export const useVi = () => {
  const context = useContext(ViContext);
  if (context === undefined) throw new ViError("useVi must be used within a ViProvider");
  return context;
};

export const ViProvider = ({
  children,
  AuthContext,
}: {
  children: ReactElement;
  AuthContext: Auth0ContextInterface;
}) => {
  try {
    const history = useHistory();
    const [actions, setActions] = useState<ViAction[]>([]);
    const [showUndamaged, setShowUndamaged] = useLocalStorage("showUndamaged", true);
    const [showRejected, setShowRejected] = useLocalStorage("showRejected", true);
    const [showDuplicates, setShowDuplicates] = useLocalStorage("showDuplicates", false);
    const App = useApp();

    const viInferenceFeature = useFeature("VI Inference");
    const baseURL = viInferenceFeature?.parameters.baseURL?.value as string;
    const isViInference =
      (viInferenceFeature?.active && typeof baseURL === "string" && baseURL.length > 0) ||
      false;

    const [state, rawDispatch] = useReducer(reducer, initialState);
    const dispatch = useCallback((action: ViAction) => {
      rawDispatch(action);
      setActions((actions) => [...actions, action]);
    }, []);

    const ViBackendProvider = useCallback(
      ({ children, baseURL }: { children: ReactElement; baseURL: string }) => {
        return isViInference ? (
          <ViInferenceProvider {...{ baseURL }}>{children}</ViInferenceProvider>
        ) : (
          <ViCoreProvider>{children}</ViCoreProvider>
        );
      },
      [isViInference]
    );

    useEffect(() => {
      for (const action of actions) {
        sideEffects(state, action, { history, App, dispatch });
      }
      if (actions.length) setActions([]);
    }, [AuthContext, baseURL, actions, history, dispatch, state, App]);

    const onConfidenceThresholdChange = useDebouncedCallback(
      (params: { damagedPartThreshold?: number; globalThreshold?: number }): void =>
        dispatch({
          type: ViActionType.ThresholdChanged,
          payload: { ...params },
        }),
      100
    );

    const setHoverFilter = useDebouncedCallback(
      (filters: Filter[] | ViImagePartDamage[] = []) => {
        dispatch({ type: ViActionType.SetHoverFilter, payload: { filters } });
      },
      100
    );

    const setFooterTab = (newState: string | null) =>
      dispatch({ type: ViActionType.ChangeFooterState, payload: { newState } });

    const toggleShowDuplicates = () => setShowDuplicates((state) => !state);
    const toggleShowUndamaged = () => setShowUndamaged((state) => !state);
    const toggleShowRejected = () => setShowRejected((state) => !state);

    const onImageLoad = useMemo(() => getOnImageLoad(dispatch, history), [
      history,
      dispatch,
    ]);

    const onImageDelete = (deletedImage: ViImageData) => {
      dispatch({
        type: ViActionType.ImageDeleted,
        payload: {
          deletedImage,
          newImages: state.images?.filter((img) => img.id !== deletedImage.id),
        },
      });
    };

    const resetState = useCallback(() => dispatch({ type: ViActionType.Reset }), [
      dispatch,
    ]);

    const isFilterMatch = useCallback(
      (filterProps: Filter[]): boolean | undefined =>
        !Array.isArray(filterProps) ||
        filterProps.some((filter) =>
          state.hoverFilter.some((hoverFilter) => isMatchingDamage(hoverFilter, filter))
        ),
      [state.hoverFilter]
    );

    const viewState = getViewState(state);

    return (
      <ViContext.Provider
        value={{
          ...viewState,
          dispatch,
          actions,
          onImageLoad,
          onImageDelete,
          setHoverFilter,
          onConfidenceThresholdChange,
          setFooterTab,
          resetState,
          showUndamaged,
          toggleShowUndamaged,
          showRejected,
          toggleShowRejected,
          showDuplicates,
          toggleShowDuplicates,
          isFilterMatch,
        }}
      >
        <ViBackendProvider {...{ baseURL }}>{children}</ViBackendProvider>
      </ViContext.Provider>
    );
  } catch (e) {
    //TODO:
    throw e;
  }
};

const reducer = function (state: ViState, action: ViAction): ViState {
  switch (action.type) {
    case ViActionType.ImagesAdded: {
      const { adding } = action.payload;
      return {
        ...(adding ? state : initialState),
        status: ViActionType.ImagesLoading,
        isLoading: true,
      };
    }

    case ViActionType.ImageAdded: {
      return {
        ...state,
        images: [...(state.images || []), action.payload.image],
      };
    }

    case ViActionType.ImageCompressing:
    case ViActionType.ImageLoaded:
    case ViActionType.ImageUploaded: {
      const { image } = action.payload;
      return {
        ...state,
        images: state.images?.map((img) => (img.localId === image.localId ? image : img)),
      };
    }

    case ViActionType.ViResultsReceived: {
      const parsedProcessing = parseProcessingResponse(
        state,
        action.payload.processingResponse
      );
      return {
        ...state,
        status: ViActionType.ViResultsReceived,
        caseId: action.payload.caseId,
        ...parsedProcessing,
      };
    }

    case ViActionType.ViResultsParsed: {
      return {
        ...state,
        status: ViActionType.ViResultsParsed,
        isLoading: false,
      };
    }

    case ViActionType.LoadResultsFromStorage: {
      return { ...state, status: action.type, isLoading: true };
    }

    case ViActionType.LoadResultsFromStorageSuccess: {
      return {
        ...action.payload.viState,
        status: action.type,
        isLoading: false,
        footerTab: null,
        hoverFilter: initialState.hoverFilter,
      };
    }

    case ViActionType.PartsterResultsReceived: {
      return {
        ...state,
        status: ViActionType.PartsterResultsReceived,
        ...action.payload,
      };
    }

    case ViActionType.ImageDeleted: {
      const { deletedImage } = action.payload;
      return {
        ...state,
        images: state.images?.filter((img) => img.id !== deletedImage.id),
        isLoading: true,
      };
    }

    case ViActionType.ChangeFooterState: {
      return { ...state, footerTab: action.payload.newState };
    }

    case ViActionType.SetHoverFilter: {
      return { ...state, hoverFilter: getFilterProperties(action.payload.filters) };
    }

    case ViActionType.ThresholdChanged: {
      const thresholds = {
        ...state.thresholds,
        ...action.payload,
      };
      return { ...state, thresholds };
    }

    case ViActionType.Error:
      return { ...state, isLoading: false };

    default: {
      return state;
    }
  }
};

const sideEffects = async (
  state: ViState,
  action: ViAction,
  {
    history,
    App,
    dispatch,
  }: { history: History; App: AppContext; dispatch: Dispatch<ViAction> }
) => {
  try {
    switch (action.type) {
      case ViActionType.ImagesAdded: {
        const { adding } = action.payload;
        if (!adding) history.push("/vi");
        break;
      }

      case ViActionType.ViResultsReceived: {
        const { caseId, processingId } = state;
        storeViProcessingState(state, caseId, processingId);
        dispatch({
          type: ViActionType.ViResultsParsed,
        });
        break;
      }

      case ViActionType.PartsterResultsReceived: {
        const { caseId, processingId } = state;
        storeViProcessingState(state, caseId, processingId);
        break;
      }

      case ViActionType.LoadResultsFromStorage: {
        const { caseId, processingId } = action.payload;
        const viState = await loadViProcessingState(caseId, processingId);
        if (viState)
          dispatch({
            type: ViActionType.LoadResultsFromStorageSuccess,
            payload: { viState, caseId, processingId },
          });
        else {
          const error = new ViUnableToLoadResultsFromStorage({
            payload: { path: history.location.pathname },
          });
          throw error;
        }
        break;
      }

      case ViActionType.Error: {
        const { error } = action.payload;
        if (error.type === ViErrorType.Error) history.replace("/");
      }
    }
  } catch (error) {
    if (!error.actionType) error.actionType = action.type;
    App.handleError && App.handleError(error);
    dispatch({ type: ViActionType.Error, payload: { error } });
  }
};

export const getOnImageLoad = (dispatch: Dispatch<ViAction>, history: History) =>
  async function onImageLoad(acceptedFiles: File[], add?: boolean) {
    const imagesToUpload = acceptedFiles.map((file) => {
      const hash = uuidv4();
      return {
        id: hash,
        localId: hash,
        status: ViImageStatus.LOADING,
        filename: file.name,
        data: "",
        file,
      };
    });
    dispatch({
      type: ViActionType.ImagesAdded,
      payload: { imagesToUpload, adding: add },
    });
    const imagePromises: Promise<any>[] = imagesToUpload.map(
      ({ file, ...image }, i, arr) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = async ({ target }) => {
            if (target && typeof target.result === "string") {
              image.data = target.result;
              dispatch({
                type: ViActionType.ImageAdded,
                payload: { image },
              });
              const compressionPromise = compressImage(file);
              dispatch({
                type: ViActionType.ImageCompressing,
                payload: { image: { ...image, status: ViImageStatus.COMPRESSING } },
              });
              const { dataUrl, isCompressed } = await compressionPromise;
              const compressedImage = {
                ...image,
                status: ViImageStatus.LOADED,
                data: dataUrl,
                isCompressed,
              };
              dispatch({
                type: ViActionType.ImageLoaded,
                payload: {
                  image: compressedImage,
                },
              });
              resolve(compressedImage);
            }
            reject();
          };
          reader.readAsDataURL(file);
        });
      }
    );
    const images = await Promise.all(imagePromises);
    dispatch({
      type: ViActionType.ImagesLoaded,
      payload: { images },
    });
  };

export const getViewState = (state: ViState): ViState => {
  const { aggregatedPartDamages, claimDamagedParts, images, bundleParts } = state || {};
  const { globalThreshold } = state.thresholds;
  const aggregatedPartDamageThreshold = globalThreshold;
  const damagedPartSideThreshold = globalThreshold;
  const physicalPartThreshold = globalThreshold;
  const workActionThreshold = globalThreshold;
  const operationThreshold = globalThreshold;
  const imagePartDamageThreshold = 0;

  const filteredClaimDamagedParts = claimDamagedParts
    .map((cPD) => {
      const physicalParts = cPD.physicalParts
        .filter((pP) => pP.p >= physicalPartThreshold)
        .map((pP) => ({
          ...pP,
          workActionResults: pP.workActionResults
            .filter((wA) => wA.p >= workActionThreshold)
            .sort((a, b) => b.p - a.p)
            .sort((a, b) => {
              const order: Record<string, number> = {
                REPAIR: 0,
                REPLACE: 0,
                PAINT: 1,
                "REMOVE-INSTALL": 2,
                OTHER: 2,
              };
              return order[a.workAction] - order[b.workAction];
            })
            .map((wA, i) => ({
              ...(i === 0 ? { ...wA, isPrimary: true } : wA),
              operations: wA.operations.filter(
                (operation) => operation.p >= operationThreshold
              ),
            })),
        }));

      return {
        ...cPD,
        objectDetectionLabels: [
          ...new Set(
            physicalParts
              .flatMap((pP) => pP.objectDetectionLabels)
              .filter(Boolean) as string[]
          ),
        ],
        p: Math.max(...physicalParts.map(({ p }) => p)),
        physicalParts,
      };
    })
    .filter(
      (cDP) =>
        cDP.p >= damagedPartSideThreshold &&
        (!cDP.sideResult || cDP.sideResult.p >= damagedPartSideThreshold)
    );

  let radiatorGrille: ViPhysicalPart | null = null;
  let frontBumperIndex: number | null = null;
  for (const [i, cPD] of filteredClaimDamagedParts.entries()) {
    if (
      cPD.partGroupName === "REAR BUMPER" &&
      cPD.physicalParts.length === 1 &&
      cPD.physicalParts[0].genericPartName === "RADIATOR GRILLE"
    ) {
      radiatorGrille = cPD.physicalParts[0];
      filteredClaimDamagedParts.splice(i, 1);
    }

    if (cPD.partGroupName === "FRONT BUMPER") frontBumperIndex = i;
  }
  if (radiatorGrille && frontBumperIndex) {
    const frontBumper = filteredClaimDamagedParts[frontBumperIndex];
    frontBumper.physicalParts.push(radiatorGrille);
    frontBumper.objectDetectionLabels = [
      ...new Set(
        frontBumper.physicalParts
          .flatMap((pP) => pP.objectDetectionLabels)
          .filter(Boolean) as string[]
      ),
    ];
  }

  const filteredAggregatedPartDamages = aggregatedPartDamages?.filter(
    (aPD) =>
      aPD.p >= aggregatedPartDamageThreshold &&
      filteredClaimDamagedParts.map((cDP) => cDP.id).includes(aPD.claimDamagedPart.id)
  );

  const filteredAggregatedPartDamageIds = filteredAggregatedPartDamages.map(
    ({ id }) => id
  );
  const filteredImagePartDamageIds = images
    .flatMap((image) => image.damages)
    .filter(
      (iPD): iPD is ViImagePartDamage =>
        iPD !== undefined && iPD.p >= imagePartDamageThreshold
    )
    .map(({ id }) => id);

  const filteredPhysicalParts = filteredClaimDamagedParts.flatMap(
    (cDP) => cDP.physicalParts
  );
  const numberOfDamagedParts = filteredPhysicalParts.length || undefined;
  const meanConfidence = getMean(filteredPhysicalParts.map(({ p }) => p));

  const physicalParts = filteredClaimDamagedParts.flatMap(({ physicalParts }) =>
    physicalParts.map(({ standardGuideNumber }) => standardGuideNumber)
  );
  const filteredBundleParts = bundleParts.filter(({ id }) => !physicalParts.includes(id));

  return {
    ...state,
    meanConfidence,
    numberOfDamagedParts,
    images: images.map(({ damages, ...img }) => ({
      ...img,
      damages: damages?.filter(
        (damage) =>
          filteredImagePartDamageIds.includes(damage.id) &&
          filteredAggregatedPartDamageIds.includes(damage.aggregatedPartDamage.id)
      ),
    })),
    aggregatedPartDamages: filteredAggregatedPartDamages,
    claimDamagedParts: filteredClaimDamagedParts,
    bundleParts: filteredBundleParts,
  };
};
